import { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    AlertTriangle,
    Calendar,
    Edit,
    History,
    Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { SHIFT_CHANGE_REQUEST } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { truncateText } from "../../components/common/CommonFunctions";
import {
    apiErrorMessage,
    canSubmitShiftChangeRequest,
    canViewMyShiftChangeRequests,
} from "./helpers/shiftChangeUtils";
import ShiftChangeRequestDialog from "./helpers/ShiftChangeRequestDialog";
import ShiftChangeReviewDialog from "./helpers/ShiftChangeReviewDialog";
import { SHIFT_CHANGE_STATUS_BADGE } from "./helpers/ShiftChangeReviewDialog";

const MyShiftChangeRequests = () => {
    const navigate = useNavigate();
    const { update } = useApiMethods();
    const canView = canViewMyShiftChangeRequests();
    const canSubmit = canSubmitShiftChangeRequest();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [loading, setLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const refresh = () => setServerTrigger((v) => !v);

    const openCreate = () => {
        setEditRecord(null);
        setCreateOpen(true);
    };

    const openEdit = (row) => {
        setEditRecord(row);
        setCreateOpen(true);
    };

    const openDetail = (row) => {
        setSelected(row);
        setDetailOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteTarget?.id) return;
        try {
            setLoading(true);
            const res = await update(SHIFT_CHANGE_REQUEST, {
                id: deleteTarget.id,
                status: "cancelled",
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Request cancelled");
                setDeleteTarget(null);
                refresh();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to cancel request"));
            }
        } catch (e) {
            ToastMsg("error", apiErrorMessage(e, "Failed to cancel request"));
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => [
        {
            accessor: "date",
            Header: "Date",
            sortable: true,
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
        },
        { accessor: "current_shift_name", Header: "Current Shift" },
        { accessor: "requested_shift_name", Header: "Requested Shift" },
        {
            accessor: "status",
            Header: "Status",
            Cell: ({ value }) => (
                <span className={cn("px-2 py-0.5 rounded-full text-xs border capitalize", SHIFT_CHANGE_STATUS_BADGE[value] || SHIFT_CHANGE_STATUS_BADGE.pending)}>
                    {value}
                </span>
            ),
        },
        { accessor: "reason", Header: "Reason", Cell: ({ value }) => truncateText(value, 36) },
        {
            accessor: "admin_comment",
            Header: "Review note",
            Cell: ({ value }) => truncateText(value, 32, "240px"),
        },
        {
            accessor: "requested_on",
            Header: "Submitted",
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Action",
            align: "right",
            fixed: "right",
            Cell: ({ row }) => {
                const record = row?.original ?? row;

                if (record.status === "pending") {
                    return (
                        <div className="flex items-center justify-end gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-md bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700"
                                title="Edit request"
                                onClick={() => openEdit(record)}
                            >
                                <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700"
                                title="Cancel request"
                                onClick={() => setDeleteTarget(record)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    );
                }

                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="View request details"
                        onClick={() => openDetail(record)}
                    >
                        <History className="h-4 w-4 text-sidebar-primary" />
                    </Button>
                );
            },
        },
    ], []);

    if (!canView) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access denied</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                        You do not have permission to view shift change requests. Ask your administrator to enable
                        Attendance → Shift Change Requests in User Permissions.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <div className="flex justify-between items-center mb-4">
                <ModuleTitleCard
                    Icon={Calendar}
                    title="My Shift Change Requests"
                    subtitle="Track and manage your shift reassignment requests"
                    totalRecords={totalRecords}
                />
                <div className="flex gap-2">
                    {canSubmit && (
                        <Button
                            size="sm"
                            className="bg-sidebar text-white hover:bg-sidebar-primary"
                            onClick={openCreate}
                        >
                            New Request
                        </Button>
                    )}
                </div>
            </div>

            <p className="text-xs text-muted-foreground mb-3">
                You can also raise a request from{" "}
                <button type="button" className="text-sidebar-primary hover:underline" onClick={() => navigate("/calendar")}>
                    Calendar
                </button>
                {" "}or{" "}
                <button type="button" className="text-sidebar-primary hover:underline" onClick={() => navigate("/home")}>
                    Home → Attendance
                </button>
                . Activity for each request appears under your profile Activity tab.
            </p>

            <Reactable
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={SHIFT_CHANGE_REQUEST}
                serverSide
                serverTrigger={serverTrigger}
                filter={{ mine_only: true }}
                rowSize="sm"
                isCSVRequired={false}
                isPDFRequired={false}
            />

            <ShiftChangeRequestDialog
                open={createOpen}
                onClose={() => {
                    setCreateOpen(false);
                    setEditRecord(null);
                }}
                onSuccess={refresh}
                initialDate={editRecord?.date || dayjs().format("YYYY-MM-DD")}
                lockDate={Boolean(editRecord)}
                currentShiftLabel={editRecord?.current_shift_name || null}
                hasAttendance
            />

            <ConfirmPopupModal
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Cancel shift change request?"
                description={`This will cancel your pending request for ${deleteTarget?.date ? dayjs(deleteTarget.date).format("DD MMM YYYY") : "this date"}.`}
                type="danger"
                confirmLabel="Cancel request"
                cancelLabel="Keep"
                loading={loading}
                destructive
            />

            <ShiftChangeReviewDialog
                open={detailOpen}
                onClose={() => {
                    setDetailOpen(false);
                    setSelected(null);
                }}
                selected={selected}
                readOnly
                hideEmployeeHeader
            />
        </ContainerCard>
    );
};

export default MyShiftChangeRequests;
